import { api } from "@/lib/api";
import { compressImage } from "@/lib/compressImage";
import { captureVideoPoster } from "@/lib/videoPoster";
import { computeFingerprint } from "@/lib/fingerprint";

// R2 multipart requires every part except the last to be at least 5MiB; 8MiB
// keeps part count (and request count) reasonable for a multi-hundred-MB video
// without pushing close to that floor.
const CHUNK_SIZE = 8 * 1024 * 1024;
const MAX_RETRIES = 3;
// Longer than the global default (20s): an 8MB chunk can genuinely take a while
// on a slow venue connection, and this needs to outlast a real-but-slow upload
// without outlasting a truly stalled one forever (see api.js for why a timeout
// exists here at all).
const CHUNK_TIMEOUT_MS = 60_000;
const MAX_VIDEO_BYTES = 2 * 1024 * 1024 * 1024; // 2GB

async function putWithRetry(url, chunk, onProgress) {
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      const { data } = await api.put(url, chunk, {
        headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: onProgress,
        timeout: CHUNK_TIMEOUT_MS,
      });
      return data;
    } catch (err) {
      attempt++;
      if (attempt > MAX_RETRIES) throw err;
      await new Promise((r) => setTimeout(r, 600 * attempt)); // backoff: flaky venue wifi gets a few tries
    }
  }
}

// Uploads a video in independently-retryable chunks straight to R2 (via the
// Worker's multipart routes), with a real poster frame captured and uploaded
// alongside it. onProgress receives (uploadedBytes, totalBytes). Returns
// { photo, duplicate } -- when duplicate is true, no chunk was ever sent: the
// exact same file was already found server-side at `init` time.
export async function uploadVideoChunked(slug, file, { caption, guestName, onProgress }) {
  if (file.size > MAX_VIDEO_BYTES) {
    throw new Error(`Video troppo grande (max 2GB, questo è ${(file.size / 1024 / 1024 / 1024).toFixed(1)}GB)`);
  }
  const fingerprint = await computeFingerprint(file);
  const { data: init } = await api.post(`/public/${slug}/videos/init`, {
    content_type: file.type || "video/mp4",
    fingerprint,
    size_bytes: file.size,
  });
  if (init.duplicate) return { photo: init.photo, duplicate: true };
  const { photo_id: photoId, key, upload_id: uploadId } = init;

  // Runs alongside the chunked upload rather than before/after it -- it's tiny,
  // so it finishes well within the time the video itself takes.
  const posterTask = (async () => {
    try {
      const poster = await captureVideoPoster(file);
      const thumb = await compressImage(poster, 480, 0.72);
      const fd = new FormData();
      fd.append("thumb", thumb);
      await api.put(`/public/${slug}/videos/${photoId}/thumb`, fd);
      return true;
    } catch {
      return false; // the gallery falls back to using the video itself as its own thumbnail
    }
  })();

  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const parts = [];
  let uploadedBytes = 0;

  try {
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(file.size, start + CHUNK_SIZE);
      const chunk = file.slice(start, end);
      const partNumber = i + 1;
      const baseBytes = uploadedBytes;

      const data = await putWithRetry(
        `/public/${slug}/videos/${uploadId}/parts/${partNumber}?key=${encodeURIComponent(key)}`,
        chunk,
        (e) => onProgress?.(baseBytes + e.loaded, file.size),
      );
      parts.push({ part_number: data.part_number, etag: data.etag });
      uploadedBytes = end;
      onProgress?.(uploadedBytes, file.size);
    }
  } catch (err) {
    api.post(`/public/${slug}/videos/${uploadId}/abort?key=${encodeURIComponent(key)}`).catch(() => {});
    throw err;
  }

  const hasThumb = await posterTask;
  const { data: photo } = await api.post(`/public/${slug}/videos/${uploadId}/complete`, {
    photo_id: photoId,
    key,
    parts,
    caption,
    guest_name: guestName,
    has_thumb: hasThumb,
    fingerprint,
  });
  return { photo, duplicate: false };
}
